import { doc, getDoc, getDocs, collection, setDoc, deleteDoc, query, where, serverTimestamp } from 'firebase/firestore';
import { firebaseFirestore } from '@/infrastructure/firebase/firebaseFirestore.js';
import { type Favorite } from '@k-ucap/shared';
import { buildConverter } from './firestoreUtils.js';
import type { FavoriteRepository } from '@/infrastructure/repositories/RepositoryInterfaces.js';

const FAVORITES = 'favorites';

const converter = buildConverter<Favorite>(
  (data) => data as Favorite,
  (model) => ({ ...model, createdAt: serverTimestamp() })
);

export class FirestoreFavoriteRepository implements FavoriteRepository {
  async findAll(): Promise<Favorite[]> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const q = query(collection(db, FAVORITES)).withConverter(converter);
    const snap = await getDocs(q);
    return snap.docs.map((d) => d.data());
  }

  async findById(id: string): Promise<Favorite | undefined> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const snap = await getDoc(doc(db, FAVORITES, id).withConverter(converter));
    return snap.data();
  }

  async findByUserId(uid: string): Promise<Favorite[]> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const q = query(collection(db, FAVORITES), where('uid', '==', uid)).withConverter(converter);
    const snap = await getDocs(q);
    return snap.docs.map((d) => d.data());
  }

  async findByUserAndAsset(uid: string, assetType: string, assetId: string): Promise<Favorite | undefined> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const q = query(
      collection(db, FAVORITES),
      where('uid', '==', uid),
      where('assetType', '==', assetType),
      where('assetId', '==', assetId)
    ).withConverter(converter);
    const snap = await getDocs(q);
    return snap.docs[0]?.data();
  }

  async save(favorite: Favorite): Promise<Favorite> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    await setDoc(doc(db, FAVORITES, favorite.id).withConverter(converter), favorite);
    return favorite;
  }

  async delete(id: string): Promise<void> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    await deleteDoc(doc(db, FAVORITES, id));
  }
}

export const createFirestoreFavoriteRepository = (): FirestoreFavoriteRepository => new FirestoreFavoriteRepository();
